import { type GradeCategory } from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { BookOpenIcon, FolderIcon, LayersIcon } from "lucide-react";

type CategoriesSectionProps = {
  categories: GradeCategory[];
  className?: string;
};

export function CategoriesSection({
  categories,
  className,
}: CategoriesSectionProps) {
  if (!categories?.length) return null;

  // Get grade name from first category (all categories are for the same grade)
  const gradeName = categories[0]?.grade?.name;
  if (!gradeName) return null;

  return (
    <section className={className}>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayersIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">
              {trans("grades.gradeCategories", {
                grade: gradeName,
              })}
            </h2>
          </div>
          {categories.length > 8 && (
            <BaseLink
              to={URLS.grades.categories}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 transition-colors hover:text-primary-700 dark:hover:text-primary-300"
            >
              {trans("viewAll")}
            </BaseLink>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {trans("grades.gradeCategoriesListDescription", {
            grade: gradeName,
          })}
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {categories.slice(0, 8).map((category) => (
          <CategoryCard key={category.id} category={category} />
        ))}
      </div>
    </section>
  );
}

type CategoryCardProps = {
  category: GradeCategory;
};

function CategoryCard({ category }: CategoryCardProps) {
  const { name, image, subject, total } = category;

  return (
    <BaseLink
      to={URLS.grades.viewCategory(category)}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all duration-300 hover:scale-[1.025]"
    >
      {/* Image Section */}
      <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-gray-700 dark:to-gray-600">
        {image?.url ? (
          <img
            src={image.url}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <FolderIcon className="h-16 w-16 text-primary-400" />
          </div>
        )}

        {/* Lessons count overlay */}
        <div className="absolute bottom-2 right-2 rounded-full bg-black/75 px-3 py-1 text-xs font-medium text-white">
          {trans("grades.totalLessons", {
            count: total?.lessons,
          })}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex-1">
          <h3 className="mb-2 line-clamp-2 text-lg font-bold text-gray-900 dark:text-gray-100">
            {name}
          </h3>

          {subject && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <BookOpenIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span className="font-medium">{subject.name}</span>
            </div>
          )}
        </div>

        {/* Bottom Action Area */}
        <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 px-4 py-2.5 text-sm font-semibold">
            <BookOpenIcon className="h-4 w-4" />
            {trans("startLearning")}
          </div>
        </div>
      </div>
    </BaseLink>
  );
}
